// Databáze produktů — sdílený katalog napříč všemi tools.
// Zdroj: Zboží.cz XML feed, 1× denně sync. Admin může spustit ručně.

import { Toast } from '@/components/Toast';
import { ApiError, apiFetch } from '@/lib/apiFetch';
import { fmtDateTime, fmtDuration, fmtNum } from '@/lib/format';
import { hasModule } from '@/lib/modules';
import type { MeResponse } from '@sklad-sync/shared';
import { useCallback, useEffect, useRef, useState } from 'react';

type Props = {
  user: MeResponse;
};

type Product = {
  shopitem_id: number;
  eshop_id: string;
  parent_code: string | null;
  name: string;
  url: string | null;
  image_url: string | null;
  visible: number;
  item_type: string;
  last_synced_at: number;
};

type FeedRun = {
  id: number;
  eshop_id: string;
  run_at: number;
  status: 'success' | 'partial' | 'failed';
  items_fetched: number;
  items_upserted: number;
  duration_ms: number | null;
  error_message: string | null;
};

const PAGE_SIZE = 50;

const ESHOPS: { code: string; label: string }[] = [
  { code: 'v-mart-cz', label: 'CZ' },
  { code: 'v-mart-sk', label: 'SK' },
];

export function ProductDatabasePage({ user }: Props) {
  const isAdmin = hasModule(user.modules, 'products_db');
  const [eshopId, setEshopId] = useState<string>(ESHOPS[0]?.code ?? 'v-mart-cz');
  const [products, setProducts] = useState<Product[] | null>(null);
  const [total, setTotal] = useState<number>(0);
  const [matched, setMatched] = useState<number>(0);
  const [page, setPage] = useState<number>(0); // 0-indexed
  const [runs, setRuns] = useState<FeedRun[]>([]);
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [toast, setToast] = useState<{ msg: string; kind: 'success' | 'error' } | null>(null);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadProducts = useCallback(
    async (q: string, pageIdx: number) => {
      try {
        const params = new URLSearchParams();
        if (q) params.set('q', q);
        params.set('eshop_id', eshopId);
        params.set('limit', String(PAGE_SIZE));
        params.set('offset', String(pageIdx * PAGE_SIZE));
        const data = await apiFetch<{
          products: Product[];
          total: number;
          matched: number;
          offset: number;
          limit: number;
        }>(`/api/propojeni/products?${params.toString()}`);
        setProducts(data.products);
        setTotal(data.total);
        setMatched(data.matched);
      } catch (e: unknown) {
        setError(e instanceof ApiError ? e.message : 'Chyba načítání produktů');
      }
    },
    [eshopId],
  );

  const loadRuns = useCallback(async () => {
    try {
      const data = await apiFetch<{ runs: FeedRun[] }>('/api/propojeni/feed-runs');
      setRuns(data.runs);
    } catch {
      // log běhů není kritický
    }
  }, []);

  useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  // Debounce vyhledávání (300ms) + reset stránky při změně dotazu
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setPage(0);
      loadProducts(query, 0);
    }, 300);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, eshopId]);

  // Když se změní stránka (mimo search), načti
  useEffect(() => {
    loadProducts(query, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  async function runSync() {
    if (!confirm('Spustit sync feedu teď? Stáhne aktuální produkty ze Zboží.cz feedu.')) return;
    setSyncing(true);
    try {
      const r = await apiFetch<{
        status: string;
        items_fetched: number;
        items_upserted: number;
        duration_ms: number;
      }>(`/api/propojeni/sync-feed?force=true&eshop_id=${encodeURIComponent(eshopId)}`, {
        method: 'POST',
      });
      setToast({
        msg: `Sync ${r.status} — ${r.items_upserted}/${r.items_fetched} produktů (${fmtDuration(r.duration_ms)})`,
        kind: r.status === 'success' ? 'success' : 'error',
      });
      await Promise.all([loadProducts(query, page), loadRuns()]);
    } catch (e: unknown) {
      setToast({
        msg: e instanceof ApiError ? e.message : 'Sync selhal',
        kind: 'error',
      });
    } finally {
      setSyncing(false);
    }
  }

  const eshopRuns = runs.filter((r) => r.eshop_id === eshopId);
  const lastRun = eshopRuns[0];
  const pageCount = Math.max(Math.ceil(matched / PAGE_SIZE), 1);
  const fromIdx = matched === 0 ? 0 : page * PAGE_SIZE + 1;
  const toIdx = Math.min((page + 1) * PAGE_SIZE, matched);

  return (
    <>
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-text">Databáze produktů</h1>
          <p className="text-sm text-text-secondary mt-0.5 max-w-2xl">
            Sdílený katalog produktů ze Zboží.cz feedu. Aktualizuje se denně v 6:00. Slouží jako
            zdroj pro propojování produktů, statistiky a další interní nástroje.
          </p>
        </div>
        {isAdmin && (
          <button
            type="button"
            onClick={runSync}
            disabled={syncing}
            className="bg-action hover:bg-action-dark text-white text-sm font-semibold rounded-lg px-6 py-2.5 transition-colors cursor-pointer disabled:opacity-50"
          >
            {syncing ? 'Synchronizuji…' : 'Spustit sync teď'}
          </button>
        )}
      </div>

      <div className="mb-4 inline-flex rounded-lg border border-border bg-bg p-1">
        {ESHOPS.map((e) => (
          <button
            key={e.code}
            type="button"
            onClick={() => setEshopId(e.code)}
            className={`text-sm font-semibold rounded-md px-4 py-1.5 transition-colors cursor-pointer ${
              eshopId === e.code
                ? 'bg-surface text-primary shadow-sm'
                : 'text-text-secondary hover:text-text'
            }`}
          >
            {e.label}
          </button>
        ))}
      </div>

      {/* Info pruh s posledním během */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-5">
        <Stat label="Produktů v DB" value={fmtNum(total)} />
        <Stat label="Poslední sync" value={lastRun ? fmtDateTime(lastRun.run_at) : '—'} />
        <Stat
          label="Stav"
          value={
            lastRun
              ? lastRun.status === 'success'
                ? '✓ OK'
                : lastRun.status === 'partial'
                  ? '⚠ Částečně'
                  : '✕ Selhalo'
              : '—'
          }
          tone={
            lastRun?.status === 'success'
              ? 'success'
              : lastRun?.status === 'failed'
                ? 'danger'
                : 'default'
          }
        />
        <Stat label="Délka" value={lastRun ? fmtDuration(lastRun.duration_ms) : '—'} />
      </div>

      {lastRun?.error_message && (
        <div className="text-sm text-warning bg-warning-light border border-warning/30 rounded-lg px-4 py-3 mb-4">
          <strong>Poslední běh hlásil:</strong> {lastRun.error_message}
        </div>
      )}

      {/* Vyhledávač + počitadlo */}
      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <input
          type="text"
          placeholder="Vyhledat podle kódu nebo názvu (např. L1080, legíny)"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="flex-1 min-w-[280px] max-w-md text-sm text-text placeholder:text-text-muted bg-surface border border-border rounded-lg px-3.5 py-2.5 outline-none focus:border-primary focus:ring-2 focus:ring-primary-100 transition-all"
        />
        <div className="text-sm text-text-secondary">
          {query ? (
            <>
              Nalezeno <strong className="text-text">{fmtNum(matched)}</strong> / {fmtNum(total)}{' '}
              produktů
            </>
          ) : (
            <>
              Zobrazeno{' '}
              <strong className="text-text">
                {fromIdx}–{toIdx}
              </strong>{' '}
              z {fmtNum(total)}
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="text-sm text-danger bg-danger-light border border-danger/30 rounded-lg px-4 py-3 mb-4">
          {error}
        </div>
      )}

      {/* Tabulka produktů */}
      <div className="bg-surface rounded-xl shadow-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-bg border-b border-border text-text-secondary">
            <tr>
              <th className="text-left font-semibold px-4 py-3 w-16">Foto</th>
              <th className="text-left font-semibold px-4 py-3 w-24">ID</th>
              <th className="text-left font-semibold px-4 py-3 w-32">Kód</th>
              <th className="text-left font-semibold px-4 py-3">Název</th>
              <th className="text-left font-semibold px-4 py-3 w-24">Stav</th>
              <th className="text-left font-semibold px-4 py-3 w-24">URL</th>
              <th className="text-right font-semibold px-4 py-3 w-40">Sync</th>
            </tr>
          </thead>
          <tbody>
            {products === null && (
              <tr>
                <td colSpan={7} className="text-center text-text-muted py-8">
                  Načítám…
                </td>
              </tr>
            )}
            {products && products.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center text-text-muted py-8">
                  {query ? 'Žádné výsledky pro daný dotaz.' : 'Databáze je prázdná. Spusť sync.'}
                </td>
              </tr>
            )}
            {products?.map((p) => (
              <tr
                key={p.shopitem_id}
                className="border-b border-border last:border-0 hover:bg-bg/50"
              >
                <td className="px-4 py-2">
                  {p.image_url ? (
                    <img
                      src={p.image_url}
                      alt=""
                      className="w-10 h-10 rounded object-cover bg-bg"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded bg-bg border border-border" />
                  )}
                </td>
                <td className="px-4 py-2 font-mono text-text-muted text-xs">{p.shopitem_id}</td>
                <td className="px-4 py-2 font-mono text-text font-semibold">
                  {p.parent_code ?? '—'}
                </td>
                <td className="px-4 py-2 text-text">{p.name}</td>
                <td className="px-4 py-2">
                  {p.visible ? (
                    <span className="text-xs font-medium text-success">✓ Viditelný</span>
                  ) : (
                    <span className="text-xs font-medium text-text-muted">Skrytý</span>
                  )}
                </td>
                <td className="px-4 py-2">
                  {p.url ? (
                    <a
                      href={p.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline text-xs"
                    >
                      Otevřít ↗
                    </a>
                  ) : (
                    <span className="text-text-muted text-xs">—</span>
                  )}
                </td>
                <td className="px-4 py-2 text-right text-text-muted text-xs">
                  {fmtDateTime(p.last_synced_at)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pager */}
      {matched > PAGE_SIZE && (
        <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
          <div className="text-sm text-text-secondary">
            Strana <strong className="text-text">{page + 1}</strong> /{' '}
            <strong className="text-text">{pageCount}</strong>
          </div>
          <div className="flex items-center gap-1">
            <PagerBtn onClick={() => setPage(0)} disabled={page === 0} label="«" />
            <PagerBtn
              onClick={() => setPage((p) => Math.max(p - 1, 0))}
              disabled={page === 0}
              label="‹ Předchozí"
            />
            <PagerBtn
              onClick={() => setPage((p) => Math.min(p + 1, pageCount - 1))}
              disabled={page >= pageCount - 1}
              label="Další ›"
            />
            <PagerBtn
              onClick={() => setPage(pageCount - 1)}
              disabled={page >= pageCount - 1}
              label="»"
            />
          </div>
        </div>
      )}

      {/* Historie běhů */}
      {eshopRuns.length > 0 && (
        <div className="mt-8">
          <h2 className="text-lg font-semibold text-text mb-3">Historie sync běhů</h2>
          <div className="bg-surface rounded-xl shadow-card overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-bg border-b border-border text-text-secondary text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left font-semibold px-4 py-2.5">Čas</th>
                  <th className="text-left font-semibold px-4 py-2.5">Stav</th>
                  <th className="text-right font-semibold px-4 py-2.5">Načteno</th>
                  <th className="text-right font-semibold px-4 py-2.5">Uloženo</th>
                  <th className="text-right font-semibold px-4 py-2.5">Délka</th>
                  <th className="text-left font-semibold px-4 py-2.5">Chyba</th>
                </tr>
              </thead>
              <tbody>
                {eshopRuns.map((r) => (
                  <tr key={r.id} className="border-b border-border last:border-0">
                    <td className="px-4 py-2 text-text">{fmtDateTime(r.run_at)}</td>
                    <td className="px-4 py-2">
                      <StatusBadge status={r.status} />
                    </td>
                    <td className="px-4 py-2 text-right text-text">{fmtNum(r.items_fetched)}</td>
                    <td className="px-4 py-2 text-right text-text">{fmtNum(r.items_upserted)}</td>
                    <td className="px-4 py-2 text-right text-text-muted">
                      {fmtDuration(r.duration_ms)}
                    </td>
                    <td className="px-4 py-2 text-text-muted text-xs truncate max-w-xs">
                      {r.error_message ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {toast && <Toast message={toast.msg} kind={toast.kind} onClose={() => setToast(null)} />}
    </>
  );
}

function PagerBtn({
  onClick,
  disabled,
  label,
}: {
  onClick: () => void;
  disabled: boolean;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="text-sm font-medium text-text bg-surface border border-border rounded-lg px-3 py-1.5 hover:bg-surface-hover disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
    >
      {label}
    </button>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'success' | 'danger' | 'default';
}) {
  const valCls =
    tone === 'success' ? 'text-success' : tone === 'danger' ? 'text-danger' : 'text-text';
  return (
    <div className="bg-surface rounded-xl p-4 shadow-card">
      <div className="text-[11px] font-semibold text-text-muted uppercase tracking-wider">
        {label}
      </div>
      <div className={`text-lg font-bold mt-1 ${valCls}`}>{value}</div>
    </div>
  );
}

function StatusBadge({ status }: { status: 'success' | 'partial' | 'failed' }) {
  if (status === 'success') {
    return (
      <span className="text-xs font-semibold text-success bg-success-light px-2 py-0.5 rounded">
        ✓ OK
      </span>
    );
  }
  if (status === 'partial') {
    return (
      <span className="text-xs font-semibold text-warning bg-warning-light px-2 py-0.5 rounded">
        ⚠ Částečně
      </span>
    );
  }
  return (
    <span className="text-xs font-semibold text-danger bg-danger-light px-2 py-0.5 rounded">
      ✕ Selhalo
    </span>
  );
}
